import React from 'react';
import { Box, Typography, Button, IconButton, Select, MenuItem, TextField, RadioGroup, FormControlLabel, Radio } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { useFormContext } from '../context/FormContext';
import { fileToBase64 } from '../utils/fileUtils';
import {
    generatePositionSectionText,
    generateImpactionDetailsText,
    generateExtensionText,
    generateCrownSectionText,
    generateCrestalBoneText
} from '../utils/impactionTextGenerator';
import {
    DEVELOPMENT_STATUS_OPTIONS, ERUPTION_STATUS_OPTIONS, IMPACTION_TYPE_OPTIONS,
    EXTENSION_SUPERIOR_OPTIONS_MAXILLA, EXTENSION_INFERIOR_OPTIONS_MAXILLA,
    EXTENSION_MESIAL_OPTIONS_MAXILLA, EXTENSION_DISTAL_OPTIONS_MAXILLA,
    EXTENSION_BUCCOLINGUAL_OPTIONS,
    CROWN_SHAPE_OPTIONS, CROWN_MARGIN_OPTIONS, CROWN_DEFECT_OPTIONS,
    CROWN_ALIGNMENT_OPTIONS, FOLLICULAR_CHANGE_OPTIONS, CRESTAL_BONE_OPTIONS,
    PREVIOUS_INTERVENTION_OPTIONS, ADDITIONAL_FINDING_OPTIONS,
    ECTOPIC_LOCATION_OPTIONS, ROTATION_OPTIONS
} from './ImpactionConstants';

import MmInput from './MmInput';

const FieldLabel = ({ children }) => (
    <Typography sx={{ fontSize: '13px', mb: '5px' }}>
        {children}
    </Typography>
);

const TextPreviewBox = ({ text }) => {
    if (!text) return null;
    return (
        <Box sx={{ mt: 2, p: 1.5, bgcolor: '#e3f2fd', borderLeft: '4px solid #2196f3', borderRadius: '0 4px 4px 0' }}>
            <Typography variant="body2" sx={{ fontStyle: 'italic', color: '#1565c0' }}>
                {text}
            </Typography>
        </Box>
    );
};

const ImpactionTab_Position_Maxilla = ({ selectedTooth, setSelectedTooth }) => {
    const { state, actions } = useFormContext();
    const toothData = state.impactionData?.[selectedTooth] || {};
    const positionData = toothData.position || {};

    const availableTeeth = ['13', '23'];

    // Generate Text Per Section (Live)
    const posText = generatePositionSectionText({ ...positionData, tooth: selectedTooth });
    const impDetailsText = generateImpactionDetailsText({ ...positionData, tooth: selectedTooth });
    const extText = generateExtensionText(positionData);
    const crownText = generateCrownSectionText(positionData);
    const boneText = generateCrestalBoneText(positionData);

    const updateData = (field, value) => {
        actions.setImpactionData({
            ...state.impactionData,
            [selectedTooth]: {
                ...toothData,
                position: {
                    ...positionData,
                    [field]: value
                }
            }
        });
    };

    const handleToothSelection = (event) => {
        const newTooth = event.target.value;
        if (newTooth && setSelectedTooth) {
            setSelectedTooth(newTooth);
        }
    };

    const handleImageUpload = async (key) => {
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = 'image/*';
        input.onchange = async (e) => {
            const file = e.target.files?.[0];
            if (!file) return;

            try {
                const base64 = await fileToBase64(file);
                const fileData = { base64, name: file.name, type: file.type };
                updateData(key, fileData);
            } catch (error) {
                console.error("Error converting file to Base64:", error);
            }
        };
        input.click();
    };

    const handleRemoveImage = (key) => {
        updateData(key, null);
    };

    const renderImageSection = (label, key, fileData, height = '250px') => {
        const preview = fileData ? fileData.base64 : null;
        return (
            <Box sx={{ width: '350px', flexShrink: 0 }}>
                <Box sx={{ position: 'relative', mb: '15px' }}>
                    <Box sx={{ width: '350px', height: height, border: '2px solid black', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'grey.200', position: 'relative', overflow: 'hidden' }}>
                        {preview && (
                            <IconButton size="small" color="error" onClick={() => handleRemoveImage(key)} sx={{ position: 'absolute', top: 8, right: 8, bgcolor: 'background.paper', '&:hover': { bgcolor: 'grey.100' }, zIndex: 1 }}>
                                <DeleteIcon fontSize="small" />
                            </IconButton>
                        )}
                        {preview ? <img src={preview} alt={label} style={{ width: '100%', height: '100%', objectFit: 'contain' }} /> : <Typography variant="body2" color="text.secondary">{label}</Typography>}
                    </Box>
                    {fileData && <Typography variant="caption" sx={{ color: 'success.main', fontSize: 10, display: 'block', mt: 0.5 }}>{fileData.name}</Typography>}
                </Box>
                <Button variant={preview ? "outlined" : "contained"} fullWidth color={preview ? "secondary" : "primary"} onClick={() => handleImageUpload(key)} sx={{ mb: '15px' }}>{preview ? 'Replace Image' : 'Select Image'}</Button>
            </Box>
        );
    };

    const sectionBoxStyle = { border: '1px solid #e0e0e0', borderRadius: '4px', mb: '20px', p: '20px' };
    const sectionHeaderStyle = { fontSize: '18px', fontWeight: 'bold', mb: '15px', pb: '10px' };
    const inputStyle = { '& .MuiOutlinedInput-root': { borderRadius: 0 }, '& .MuiOutlinedInput-input': { padding: '8px 14px', fontSize: '14px' } };

    return (
        <Box>
            <Typography variant="h6" gutterBottom color="primary" sx={{ mb: 2 }}>Tooth Position and Development (Maxilla {selectedTooth})</Typography>

            {/* SECTION 1: POSITION & DEVELOPMENT */}
            <Box sx={sectionBoxStyle}>
                <Typography sx={sectionHeaderStyle}>DEVELOPMENT</Typography>
                <Box sx={{ display: 'flex', gap: '25px', flexDirection: { xs: 'column', lg: 'row' } }}>
                    {renderImageSection('Panoramic Image', 'panImage', positionData.panImage)}
                    <Box sx={{ flex: 1, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', alignContent: 'start' }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                            <FieldLabel>Active Tooth</FieldLabel>
                            <Select value={selectedTooth} onChange={handleToothSelection} size="small" sx={inputStyle}>
                                {availableTeeth.map((tooth) => <MenuItem key={tooth} value={tooth}>Tooth {tooth}</MenuItem>)}
                            </Select>
                        </Box>
                        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                            <FieldLabel>Development Status</FieldLabel>
                            <Select value={positionData.development || ''} onChange={(e) => updateData('development', e.target.value)} displayEmpty size="small" sx={inputStyle}>
                                {DEVELOPMENT_STATUS_OPTIONS.map(opt => <MenuItem key={opt} value={opt}>{opt}</MenuItem>)}
                            </Select>
                        </Box>
                        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                            <FieldLabel>Eruption Status</FieldLabel>
                            <Select value={positionData.eruption || ''} onChange={(e) => updateData('eruption', e.target.value)} displayEmpty size="small" sx={inputStyle}>
                                {ERUPTION_STATUS_OPTIONS.map(opt => <MenuItem key={opt} value={opt}>{opt}</MenuItem>)}
                            </Select>
                        </Box>
                        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                            <FieldLabel>Rotation</FieldLabel>
                            <Select value={positionData.rotation || ''} onChange={(e) => updateData('rotation', e.target.value)} displayEmpty size="small" sx={inputStyle}>
                                {ROTATION_OPTIONS.map(opt => <MenuItem key={opt} value={opt}>{opt}</MenuItem>)}
                            </Select>
                        </Box>
                    </Box>
                </Box>
                <TextPreviewBox text={posText} />
            </Box>

            {/* SECTION 2: IMPACTION DETAILS */}
            <Box sx={sectionBoxStyle}>
                <Typography sx={sectionHeaderStyle}>IMPACTION DETAILS</Typography>
                <Box sx={{ display: 'flex', gap: '25px', flexDirection: { xs: 'column', lg: 'row' } }}>
                    <Box>
                        {renderImageSection('Sagittal Image', 'sagittalImage', positionData.sagittalImage)}
                        <Typography variant="body2" sx={{ my: 1, fontWeight: 'bold' }}>Angulation View:</Typography>
                        {renderImageSection('Angulation Image', 'angulationImage', positionData.angulationImage, '200px')}
                    </Box>
                    <Box sx={{ flex: 1, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', alignContent: 'start' }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gridColumn: '1 / -1' }}>
                            <FieldLabel>Impaction Type</FieldLabel>
                            <Select value={positionData.type || ''} onChange={(e) => updateData('type', e.target.value)} displayEmpty size="small" sx={inputStyle}>
                                {IMPACTION_TYPE_OPTIONS.map(opt => <MenuItem key={opt} value={opt}>{opt}</MenuItem>)}
                            </Select>
                        </Box>

                        {/* Specific inputs for types */}
                        {positionData.type === 'Mesioangular' && (
                            <Box sx={{ display: 'flex', flexDirection: 'column', p: 1, bgcolor: 'grey.50', border: '1px dashed #ccc' }}>
                                <FieldLabel>Tilt Angle (approx.)</FieldLabel>
                                <MmInput value={positionData.mesioAngle} onChange={(e) => updateData('mesioAngle', e.target.value)} placeholder="deg" adornment="°" width="100px" />
                            </Box>
                        )}
                        {positionData.type === 'Distoangular' && (
                            <Box sx={{ display: 'flex', flexDirection: 'column', p: 1, bgcolor: 'grey.50', border: '1px dashed #ccc' }}>
                                <FieldLabel>Tilt Angle (approx.)</FieldLabel>
                                <MmInput value={positionData.distoAngle} onChange={(e) => updateData('distoAngle', e.target.value)} placeholder="deg" adornment="°" width="100px" />
                            </Box>
                        )}
                        {positionData.type === 'Ectopic' && (
                            <Box sx={{ display: 'flex', flexDirection: 'column', gridColumn: '1 / -1', p: 1, bgcolor: 'grey.50', border: '1px dashed #ccc' }}>
                                <FieldLabel>Location</FieldLabel>
                                <Select value={positionData.ectopicLocation || ''} onChange={(e) => updateData('ectopicLocation', e.target.value)} displayEmpty size="small" sx={inputStyle}>
                                    {ECTOPIC_LOCATION_OPTIONS.map(opt => <MenuItem key={opt} value={opt}>{opt}</MenuItem>)}
                                </Select>
                            </Box>
                        )}
                    </Box>
                </Box>
                <TextPreviewBox text={impDetailsText} />
            </Box>

            {/* SECTION 3: EXTENSION OF TOOTH (Maxilla Specific) */}
            <Box sx={sectionBoxStyle}>
                <Typography sx={sectionHeaderStyle}>EXTENSION OF TOOTH (Maxilla)</Typography>
                <Box sx={{ display: 'flex', gap: '25px', flexDirection: { xs: 'column', lg: 'row' } }}>
                    {renderImageSection('Extension Image', 'extensionsImage', positionData.extensionsImage)}
                    <Box sx={{ flex: 1, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', alignContent: 'start' }}>

                        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                            <FieldLabel>Superiorly (towards nasal/sinus)</FieldLabel>
                            <Box display="flex" gap={1}>
                                <Select value={positionData.extSuperior || ''} onChange={(e) => updateData('extSuperior', e.target.value)} displayEmpty size="small" sx={{ ...inputStyle, flexGrow: 1 }}>
                                    {EXTENSION_SUPERIOR_OPTIONS_MAXILLA.map(opt => <MenuItem key={opt} value={opt}>{opt}</MenuItem>)}
                                </Select>
                                <MmInput value={positionData.extSuperiorMm} onChange={(e) => updateData('extSuperiorMm', e.target.value)} width="110px" />
                            </Box>
                        </Box>

                        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                            <FieldLabel>Inferiorly (towards occlusal)</FieldLabel>
                            <Box display="flex" gap={1}>
                                <Select value={positionData.extInferior || ''} onChange={(e) => updateData('extInferior', e.target.value)} displayEmpty size="small" sx={{ ...inputStyle, flexGrow: 1 }}>
                                    {EXTENSION_INFERIOR_OPTIONS_MAXILLA.map(opt => <MenuItem key={opt} value={opt}>{opt}</MenuItem>)}
                                </Select>
                                <MmInput value={positionData.extInferiorMm} onChange={(e) => updateData('extInferiorMm', e.target.value)} width="110px" />
                            </Box>
                        </Box>

                        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                            <FieldLabel>Mesially</FieldLabel>
                            <Select value={positionData.extMesial || ''} onChange={(e) => updateData('extMesial', e.target.value)} displayEmpty size="small" sx={inputStyle}>
                                {EXTENSION_MESIAL_OPTIONS_MAXILLA.map(opt => <MenuItem key={opt} value={opt}>{opt}</MenuItem>)}
                            </Select>
                        </Box>

                        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                            <FieldLabel>Distally</FieldLabel>
                            <Select value={positionData.extDistal || ''} onChange={(e) => updateData('extDistal', e.target.value)} displayEmpty size="small" sx={inputStyle}>
                                {EXTENSION_DISTAL_OPTIONS_MAXILLA.map(opt => <MenuItem key={opt} value={opt}>{opt}</MenuItem>)}
                            </Select>
                        </Box>

                        <Box sx={{ display: 'flex', flexDirection: 'column', gridColumn: '1 / -1' }}>
                            <FieldLabel>Buccopalatally</FieldLabel>
                            <Select value={positionData.extBuccolingual || ''} onChange={(e) => updateData('extBuccolingual', e.target.value)} displayEmpty size="small" sx={inputStyle}>
                                {EXTENSION_BUCCOLINGUAL_OPTIONS.map(opt => <MenuItem key={opt} value={opt}>{opt}</MenuItem>)}
                            </Select>
                        </Box>
                    </Box>
                </Box>
                <TextPreviewBox text={extText} />
            </Box>

            {/* SECTION 4: CROWN FEATURES (Same as Mandible usually) */}
            <Box sx={sectionBoxStyle}>
                <Typography sx={sectionHeaderStyle}>CROWN FEATURES</Typography>
                <Box sx={{ display: 'flex', gap: '25px', flexDirection: { xs: 'column', lg: 'row' } }}>
                    {renderImageSection('Crown / Follicular Image', 'crownImage', positionData.crownImage)}
                    <Box sx={{ flex: 1, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', alignContent: 'start' }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                            <FieldLabel>Shape</FieldLabel>
                            <Select value={positionData.crownShape || ''} onChange={(e) => updateData('crownShape', e.target.value)} displayEmpty size="small" sx={inputStyle}>
                                {CROWN_SHAPE_OPTIONS.map(opt => <MenuItem key={opt} value={opt}>{opt}</MenuItem>)}
                            </Select>
                        </Box>

                        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                            <FieldLabel>Margin</FieldLabel>
                            <Select value={positionData.crownMargin || ''} onChange={(e) => updateData('crownMargin', e.target.value)} displayEmpty size="small" sx={inputStyle}>
                                {CROWN_MARGIN_OPTIONS.map(opt => <MenuItem key={opt} value={opt}>{opt}</MenuItem>)}
                            </Select>
                        </Box>

                        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                            <FieldLabel>Defects</FieldLabel>
                            <Select value={positionData.crownDefect || ''} onChange={(e) => updateData('crownDefect', e.target.value)} displayEmpty size="small" sx={inputStyle}>
                                {CROWN_DEFECT_OPTIONS.map(opt => <MenuItem key={opt} value={opt}>{opt}</MenuItem>)}
                            </Select>
                        </Box>

                        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                            <FieldLabel>Alignment</FieldLabel>
                            <Select value={positionData.crownAlignment || ''} onChange={(e) => updateData('crownAlignment', e.target.value)} displayEmpty size="small" sx={inputStyle}>
                                {CROWN_ALIGNMENT_OPTIONS.map(opt => <MenuItem key={opt} value={opt}>{opt}</MenuItem>)}
                            </Select>
                        </Box>

                        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                            <FieldLabel>Follicular Changes</FieldLabel>
                            <Select value={positionData.follicularChanges || ''} onChange={(e) => updateData('follicularChanges', e.target.value)} displayEmpty size="small" sx={inputStyle}>
                                {FOLLICULAR_CHANGE_OPTIONS.map(opt => <MenuItem key={opt} value={opt}>{opt}</MenuItem>)}
                            </Select>
                        </Box>
                    </Box>
                </Box>
                <TextPreviewBox text={crownText} />
            </Box>

            {/* SECTION 5: CRESTAL BONE */}
            <Box sx={sectionBoxStyle}>
                <Typography sx={sectionHeaderStyle}>CRESTAL BONE</Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                    <FieldLabel>Crestal Bone Status</FieldLabel>
                    <Select value={positionData.crestalBone || ''} onChange={(e) => updateData('crestalBone', e.target.value)} displayEmpty size="small" sx={{ ...inputStyle, maxWidth: '500px' }}>
                        {CRESTAL_BONE_OPTIONS.map(opt => <MenuItem key={opt} value={opt}>{opt}</MenuItem>)}
                    </Select>
                </Box>
                <TextPreviewBox text={boneText} />
            </Box>
        </Box>
    );
};

export default ImpactionTab_Position_Maxilla;
